import { CanonicalPresentationModelV1Schema, } from "../contracts/canonical-presentation.js";
import { derivePairedChangeRequestPresentation, formatClientSafeRevisedPreviewRef, } from "./derive-change-request-presentation.js";
import { stableStringify } from "../hashing/canonical-json.js";
/** Exact Step 4 Slice C named outcomes (Review → Approval → Delivery). */
export const REVIEW_APPROVAL_DELIVERY_OUTCOMES = [
    "permission_denied",
    "source_unknown",
    "preview_awaiting_client_review",
    "change_requested",
    "change_in_progress",
    "revised_preview_ready",
    "change_unresolved",
    "approval_ready",
    "approval_stale",
    "approval_complete",
    "delivery_blocked",
    "delivery_in_progress",
    "delivery_ready",
    "records_available",
];
const PROHIBITED_CLIENT_TERMS = [
    "runId",
    "artifactSetHash",
    "exportBundleHash",
    "businessGraphRevisionId",
    "Quality Gate",
    "worker",
    "tenant",
    "schema",
    "GenerationRun",
    "BLOCKED",
    "ELIGIBLE",
    "EXPORT_QUEUED",
    "EXPORTING",
    "COMPOSING",
    "REQUESTED",
    "Departure Kit",
];
function projectIdOk(input) {
    return Boolean(input.projectId.trim());
}
function isValidReadyPreview(preview) {
    return Boolean(preview && preview.previewStatus === "READY" && preview.previewVersionId.trim().length === 64);
}
function baseModel(partial) {
    return CanonicalPresentationModelV1Schema.parse({
        schemaId: "enrein.canonicalPresentation.v1",
        ...partial,
    });
}
function classify(input) {
    if (input.accessDenial === "permission_denied") {
        return { outcome: "permission_denied", delegate: null };
    }
    const projectId = input.projectId.trim();
    if (input.latestChangeRequest) {
        if (input.latestChangeRequest.projectId.trim() !== projectId) {
            return { outcome: "source_unknown", delegate: null };
        }
        const delegate = derivePairedChangeRequestPresentation(input.latestChangeRequest);
        if (!delegate) {
            return { outcome: "source_unknown", delegate: null };
        }
        switch (delegate.agency.condition) {
            case "READY":
                return { outcome: "change_requested", delegate };
            case "IN_PROGRESS":
                return { outcome: "change_in_progress", delegate };
            case "COMPLETE":
                return { outcome: "revised_preview_ready", delegate };
            case "NEEDS_ATTENTION":
                return { outcome: "change_unresolved", delegate };
            default:
                return { outcome: "source_unknown", delegate: null };
        }
    }
    const validPreview = isValidReadyPreview(input.latestPreview);
    const approval = input.latestApproval;
    if (!approval) {
        if (!validPreview)
            return { outcome: "source_unknown", delegate: null };
        if (input.consentAvailable === true) {
            return { outcome: "approval_ready", delegate: null };
        }
        return { outcome: "preview_awaiting_client_review", delegate: null };
    }
    if (input.approvalMatchesLatestPassingArtifact !== true) {
        return { outcome: "approval_stale", delegate: null };
    }
    const dk = input.departureKit;
    if (!dk)
        return { outcome: "approval_complete", delegate: null };
    if (dk.issuanceStatus === "BLOCKED" || dk.issuanceStatus === "FAILED") {
        return { outcome: "delivery_blocked", delegate: null };
    }
    if (dk.downloadAvailable === true || dk.issuanceStatus === "READY") {
        if (input.clientLinkStatus === "HANDOFF_READY") {
            return { outcome: "records_available", delegate: null };
        }
        return { outcome: "delivery_ready", delegate: null };
    }
    return { outcome: "delivery_in_progress", delegate: null };
}
function pairForOutcome(outcome, input) {
    const projectId = input.projectId.trim();
    const previewVersionId = input.latestPreview?.previewVersionId;
    switch (outcome) {
        case "permission_denied":
            return {
                outcome,
                agency: baseModel({
                    role: "AGENCY",
                    journeyStage: "Approve and deliver",
                    condition: "NEEDS_ATTENTION",
                    title: "Access to this review was denied",
                    reason: "A review/approval access attempt failed a permission or tenant check.",
                    primaryAction: null,
                    secondaryAction: null,
                    waitingOn: null,
                    blockers: ["Access denied"],
                    progress: null,
                    expectedResult: null,
                    recovery: "Confirm the correct project and permissions before retrying.",
                    help: null,
                    relevantRecords: [],
                    advancedDetails: null,
                }),
                client: baseModel({
                    role: "CLIENT",
                    journeyStage: "Request changes or approve",
                    condition: "NEEDS_ATTENTION",
                    title: "Access is not available",
                    reason: "You do not have permission to view this review.",
                    primaryAction: null,
                    secondaryAction: null,
                    waitingOn: null,
                    blockers: [],
                    progress: null,
                    expectedResult: null,
                    recovery: "Ask your agency for a new secure link if you still need access.",
                    help: null,
                    relevantRecords: [],
                    advancedDetails: null,
                }),
            };
        case "source_unknown":
            return {
                outcome,
                agency: baseModel({
                    role: "AGENCY",
                    journeyStage: "Approve and deliver",
                    condition: "NEEDS_ATTENTION",
                    title: "Review status could not be confirmed",
                    reason: "The review/approval source returned an unrecognized or incomplete state.",
                    primaryAction: null,
                    secondaryAction: null,
                    waitingOn: null,
                    blockers: ["Review status is unknown"],
                    progress: null,
                    expectedResult: null,
                    recovery: "Nothing was marked complete. Refresh the project and try again.",
                    help: null,
                    relevantRecords: [`${projectId} — review status unknown`],
                    advancedDetails: null,
                }),
                client: baseModel({
                    role: "CLIENT",
                    journeyStage: "Request changes or approve",
                    condition: "NEEDS_ATTENTION",
                    title: "We could not confirm the current status",
                    reason: "The action was not completed and nothing was marked complete.",
                    primaryAction: null,
                    secondaryAction: null,
                    waitingOn: null,
                    blockers: [],
                    progress: null,
                    expectedResult: null,
                    recovery: "Your saved information remains available. Please check back shortly.",
                    help: null,
                    relevantRecords: [],
                    advancedDetails: null,
                }),
            };
        case "preview_awaiting_client_review":
            return {
                outcome,
                agency: baseModel({
                    role: "AGENCY",
                    journeyStage: "Review and make changes",
                    condition: "WAITING",
                    title: "Waiting for the client to review",
                    reason: "The exact preview is ready. Waiting for the client to review it.",
                    primaryAction: null,
                    secondaryAction: null,
                    waitingOn: "Client",
                    blockers: [],
                    progress: null,
                    expectedResult: "The client approves or requests changes next.",
                    recovery: null,
                    help: null,
                    relevantRecords: [formatClientSafeRevisedPreviewRef(previewVersionId)],
                    advancedDetails: null,
                }),
                client: baseModel({
                    role: "CLIENT",
                    journeyStage: "Review your website",
                    condition: "READY",
                    title: "Review your website",
                    reason: "Your exact website preview is ready for you to review.",
                    primaryAction: {
                        intent: "OPEN_CLIENT_EXACT_PREVIEW",
                        previewVersionId: previewVersionId,
                        label: "Review your website",
                    },
                    secondaryAction: null,
                    waitingOn: null,
                    blockers: [],
                    progress: null,
                    expectedResult: "You can approve it or request changes next.",
                    recovery: null,
                    help: null,
                    relevantRecords: [formatClientSafeRevisedPreviewRef(previewVersionId)],
                    advancedDetails: null,
                }),
            };
        case "approval_ready":
            return {
                outcome,
                agency: baseModel({
                    role: "AGENCY",
                    journeyStage: "Approve and deliver",
                    condition: "WAITING",
                    title: "Waiting for client approval",
                    reason: "The client can give exact approval for this preview now.",
                    primaryAction: null,
                    secondaryAction: null,
                    waitingOn: "Client",
                    blockers: [],
                    progress: null,
                    expectedResult: "Delivery preparation starts once the client approves.",
                    recovery: null,
                    help: null,
                    relevantRecords: [formatClientSafeRevisedPreviewRef(previewVersionId)],
                    advancedDetails: null,
                }),
                client: baseModel({
                    role: "CLIENT",
                    journeyStage: "Request changes or approve",
                    condition: "READY",
                    title: "Give your exact approval",
                    reason: "This is the exact website version. You can approve it now.",
                    primaryAction: {
                        intent: "GIVE_EXACT_APPROVAL",
                        previewVersionId: previewVersionId,
                        label: "Approve this version",
                    },
                    secondaryAction: null,
                    waitingOn: null,
                    blockers: [],
                    progress: null,
                    expectedResult: "Your agency prepares delivery once you approve.",
                    recovery: null,
                    help: null,
                    relevantRecords: [formatClientSafeRevisedPreviewRef(previewVersionId)],
                    advancedDetails: null,
                }),
            };
        case "approval_stale":
            return {
                outcome,
                agency: baseModel({
                    role: "AGENCY",
                    journeyStage: "Approve and deliver",
                    condition: "NEEDS_ATTENTION",
                    title: "Client approval is stale",
                    reason: "The client approved an earlier version. The approval no longer matches the current passing preview.",
                    primaryAction: null,
                    secondaryAction: null,
                    waitingOn: "Client",
                    blockers: ["Approval does not match the current preview"],
                    progress: null,
                    expectedResult: "A fresh client approval on the current version unblocks delivery.",
                    recovery: "Share the current preview with the client for a fresh approval.",
                    help: null,
                    relevantRecords: [],
                    advancedDetails: null,
                }),
                client: baseModel({
                    role: "CLIENT",
                    journeyStage: "Request changes or approve",
                    condition: "NEEDS_ATTENTION",
                    title: "Please review the current version",
                    reason: "The website has changed since your last approval. Please review and approve the current version.",
                    primaryAction: previewVersionId
                        ? {
                            intent: "OPEN_CLIENT_EXACT_PREVIEW",
                            previewVersionId,
                            label: "Review the current version",
                        }
                        : null,
                    secondaryAction: null,
                    waitingOn: null,
                    blockers: ["Your earlier approval is no longer current"],
                    progress: null,
                    expectedResult: null,
                    recovery: "Review and approve the current version to continue.",
                    help: null,
                    relevantRecords: [],
                    advancedDetails: null,
                }),
            };
        case "approval_complete":
            return {
                outcome,
                agency: baseModel({
                    role: "AGENCY",
                    journeyStage: "Approve and deliver",
                    condition: "COMPLETE",
                    title: "Client approved the exact preview",
                    reason: "The client gave exact approval. Ownership handoff can be packaged next.",
                    primaryAction: {
                        intent: "PACKAGE_HANDOFF",
                        projectId,
                        label: "Package handoff",
                    },
                    secondaryAction: null,
                    waitingOn: null,
                    blockers: [],
                    progress: { current: 6, total: 7, label: "Stage 6 of 7" },
                    expectedResult: "Packaging prepares the ownership handoff for delivery.",
                    recovery: null,
                    help: null,
                    relevantRecords: [],
                    advancedDetails: null,
                }),
                client: baseModel({
                    role: "CLIENT",
                    journeyStage: "Request changes or approve",
                    condition: "COMPLETE",
                    title: "You approved your website",
                    reason: "Thank you — your approval was recorded. Your agency is preparing your handoff.",
                    primaryAction: null,
                    secondaryAction: null,
                    waitingOn: "Agency",
                    blockers: [],
                    progress: null,
                    expectedResult: "Your ownership package will be available soon.",
                    recovery: null,
                    help: null,
                    relevantRecords: [],
                    advancedDetails: null,
                }),
            };
        case "delivery_blocked":
            return {
                outcome,
                agency: baseModel({
                    role: "AGENCY",
                    journeyStage: "Approve and deliver",
                    condition: "NEEDS_ATTENTION",
                    title: "Handoff packaging is blocked",
                    reason: "Ownership handoff packaging could not proceed.",
                    primaryAction: {
                        intent: "PACKAGE_HANDOFF",
                        projectId,
                        label: "Retry packaging",
                    },
                    secondaryAction: null,
                    waitingOn: null,
                    blockers: ["Handoff packaging is blocked"],
                    progress: null,
                    expectedResult: "Retrying packaging prepares the ownership handoff.",
                    recovery: "Retry packaging the handoff.",
                    help: null,
                    relevantRecords: [],
                    advancedDetails: null,
                }),
                client: baseModel({
                    role: "CLIENT",
                    journeyStage: "Records and ongoing care",
                    condition: "WAITING",
                    title: "Waiting for your agency",
                    reason: "Your agency is resolving an issue while preparing your ownership package.",
                    primaryAction: null,
                    secondaryAction: null,
                    waitingOn: "Agency",
                    blockers: [],
                    progress: null,
                    expectedResult: "Your ownership package will be available soon.",
                    recovery: null,
                    help: null,
                    relevantRecords: [],
                    advancedDetails: null,
                }),
            };
        case "delivery_in_progress":
            return {
                outcome,
                agency: baseModel({
                    role: "AGENCY",
                    journeyStage: "Approve and deliver",
                    condition: "IN_PROGRESS",
                    title: "Packaging the ownership handoff",
                    reason: "Handoff packaging is actively running.",
                    primaryAction: null,
                    secondaryAction: null,
                    waitingOn: null,
                    blockers: [],
                    progress: null,
                    expectedResult: "The ownership package becomes available automatically once packaging finishes.",
                    recovery: null,
                    help: null,
                    relevantRecords: [],
                    advancedDetails: null,
                }),
                client: baseModel({
                    role: "CLIENT",
                    journeyStage: "Records and ongoing care",
                    condition: "WAITING",
                    title: "Waiting for your agency",
                    reason: "Your agency is preparing your ownership package.",
                    primaryAction: null,
                    secondaryAction: null,
                    waitingOn: "Agency",
                    blockers: [],
                    progress: null,
                    expectedResult: "Your ownership package will be available soon.",
                    recovery: null,
                    help: null,
                    relevantRecords: [],
                    advancedDetails: null,
                }),
            };
        case "delivery_ready":
            return {
                outcome,
                agency: baseModel({
                    role: "AGENCY",
                    journeyStage: "Approve and deliver",
                    condition: "COMPLETE",
                    title: "Ownership handoff is ready",
                    reason: "The ownership handoff package is ready for the client to access.",
                    primaryAction: null,
                    secondaryAction: null,
                    waitingOn: null,
                    blockers: [],
                    progress: { current: 7, total: 7, label: "Stage 7 of 7" },
                    expectedResult: "The client can open their ownership records.",
                    recovery: null,
                    help: null,
                    relevantRecords: [],
                    advancedDetails: null,
                }),
                client: baseModel({
                    role: "CLIENT",
                    journeyStage: "Records and ongoing care",
                    condition: "READY",
                    title: "Your ownership package is ready",
                    reason: "Your ownership package is ready to view.",
                    primaryAction: {
                        intent: "OPEN_OWNERSHIP_RECORDS",
                        label: "Open ownership records",
                    },
                    secondaryAction: null,
                    waitingOn: null,
                    blockers: [],
                    progress: null,
                    expectedResult: "You can view and keep your ownership records.",
                    recovery: null,
                    help: null,
                    relevantRecords: [],
                    advancedDetails: null,
                }),
            };
        case "records_available":
            return {
                outcome,
                agency: baseModel({
                    role: "AGENCY",
                    journeyStage: "Ongoing care",
                    condition: "COMPLETE",
                    title: "Handoff is complete",
                    reason: "Ownership handoff is complete and records remain available to the client.",
                    primaryAction: null,
                    secondaryAction: null,
                    waitingOn: null,
                    blockers: [],
                    progress: null,
                    expectedResult: null,
                    recovery: null,
                    help: null,
                    relevantRecords: [],
                    advancedDetails: null,
                }),
                client: baseModel({
                    role: "CLIENT",
                    journeyStage: "Records and ongoing care",
                    condition: "READY",
                    title: "Your ownership records",
                    reason: "Your ownership records remain available any time.",
                    primaryAction: {
                        intent: "OPEN_OWNERSHIP_RECORDS",
                        label: "Open ownership records",
                    },
                    secondaryAction: null,
                    waitingOn: null,
                    blockers: [],
                    progress: null,
                    expectedResult: "You can view and keep your ownership records.",
                    recovery: null,
                    help: null,
                    relevantRecords: [],
                    advancedDetails: null,
                }),
            };
        default:
            throw new Error(`Unhandled Slice C outcome: ${outcome}`);
    }
}
/**
 * Fail-closed paired Agency/Client Review → Approval → Delivery presentation
 * (ENR-UX-009 Step 4 Slice C). change_* outcomes delegate entirely to
 * derivePairedChangeRequestPresentation (ENR-UX-007) — never re-derived here.
 */
export function derivePairedReviewApprovalDelivery(input) {
    if (!projectIdOk(input))
        return null;
    const classified = classify(input);
    if (!classified)
        return null;
    if (classified.delegate) {
        return { outcome: classified.outcome, agency: classified.delegate.agency, client: classified.delegate.client };
    }
    if ((classified.outcome === "preview_awaiting_client_review" ||
        classified.outcome === "approval_ready") &&
        !isValidReadyPreview(input.latestPreview)) {
        return null;
    }
    return pairForOutcome(classified.outcome, input);
}
export function canonicalizeReviewApprovalDelivery(model) {
    return stableStringify(model);
}
/** Fail-closed Client terminology scan for presentation copy. */
export function clientReviewApprovalDeliveryContainsProhibitedTerminology(model) {
    if (model.role !== "CLIENT")
        return false;
    const blob = [
        model.title,
        model.reason,
        model.expectedResult ?? "",
        model.recovery ?? "",
        model.help ?? "",
        model.advancedDetails ?? "",
        ...model.blockers,
        ...model.relevantRecords,
        model.primaryAction?.label ?? "",
    ].join("\n");
    return PROHIBITED_CLIENT_TERMS.some((term) => blob.includes(term));
}
//# sourceMappingURL=derive-review-approval-delivery.js.map